/**
 * Reconciliation service — frontend wrappers for the runStripeReconciliation
 * Cloud Function and the reconciliationReports Firestore collection.
 */

import { httpsCallable } from 'firebase/functions';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db, functions } from '../firebase';

// ── Types (mirror Cloud Function schema) ──────────────────────────────────────

export interface ReconItemFs {
  priceCents: number;
  totalLessons: number;
  totalCents: number;
  paymentStatus: string;
  playerName: string;
  coachName: string;
  offeringTitle: string;
  createdAt: string;
}

export interface ReconItemStripe {
  amountCents: number;
  status: string;
  capturedAt: string;
  refundedCents: number;
}

export interface ReconItem {
  paymentIntentId: string | null;
  /** null for Stripe-only items with no matching Firestore order */
  orderId: string | null;
  /** null for Stripe-only items */
  fs: ReconItemFs | null;
  stripe?: ReconItemStripe;
  issues: string[];
  isMatch: boolean;
}

export interface ReconAggregate {
  fs: {
    ordersWithPayment: number;
    capturedCents: number;
    transferredCents: number;
    refundedCents: number;
    freeCents: number;
  };
  stripe: {
    piCount: number;
    totalChargedCents: number;
    totalRefundedCents: number;
    unmatchedPiCount: number;
    unmatchedChargedCents: number;
  };
  diff: {
    chargedCents: number;
    refundedCents: number;
  };
}

export interface ReconSummary {
  total: number;
  matched: number;
  mismatches: number;
  missingInStripe: number;
  missingInFirestore: number;
  freeOrders: number;
}

export interface ReconReport {
  id: string;
  runAt: string;   // ISO string after Firestore timestamp conversion
  runByUid: string;
  status: 'complete' | 'error';
  error?: string;
  aggregate: ReconAggregate;
  summary: ReconSummary;
  items: ReconItem[];
}

// ── Cloud Function callable ───────────────────────────────────────────────────

export const runReconciliationFn = httpsCallable<void, { reportId: string }>(
  functions,
  'runStripeReconciliation',
  { timeout: 300_000 } // 5 min — matches server-side timeoutSeconds: 300
);

// ── Firestore reads ───────────────────────────────────────────────────────────

function convertTimestamp(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') return v;
  // Firestore Timestamp-like object
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate(): Date }).toDate().toISOString();
  }
  if (typeof v === 'object' && v !== null && 'seconds' in v) {
    return new Date((v as { seconds: number }).seconds * 1000).toISOString();
  }
  return String(v);
}

function docToReport(id: string, data: Record<string, unknown>): ReconReport {
  return {
    id,
    runAt: convertTimestamp(data.runAt),
    runByUid: data.runByUid as string ?? '',
    status: data.status as 'complete' | 'error',
    error: data.error as string | undefined,
    aggregate: data.aggregate as ReconAggregate,
    summary: data.summary as ReconSummary,
    items: (data.items ?? []) as ReconItem[],
  };
}

/** Fetch a single reconciliation report by ID. */
export async function getReconciliationReport(reportId: string): Promise<ReconReport | null> {
  const snap = await getDoc(doc(db, 'reconciliationReports', reportId));
  if (!snap.exists()) return null;
  return docToReport(snap.id, snap.data() as Record<string, unknown>);
}

/** Fetch the N most recent reconciliation reports (metadata only — no items). */
export async function getLatestReconciliationReports(n = 5): Promise<Omit<ReconReport, 'items'>[]> {
  const q = query(
    collection(db, 'reconciliationReports'),
    orderBy('runAt', 'desc'),
    limit(n)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data() as Record<string, unknown>;
    const report = docToReport(d.id, data);
    // Return without potentially large items array for the history list
    return { ...report, items: [] };
  });
}
